import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import { useStoreAuth } from "./StoreAuthContext";
import { resolveUrl } from "../../utils/storeUtils";
import { StoreMoneyDisplay } from "./components/StorePriceDisplay";
import StoreSignIn from "./components/StoreSignIn";
import "./MyOrders.css";

const STATUS_CONFIG = {
  delivered: { labelKey: "statusDelivered", color: "var(--my-orders-status-delivered, #059669)" },
  shipped: { labelKey: "statusShipped", color: "var(--my-orders-status-shipped, #2563eb)" },
  confirmed: { labelKey: "statusConfirmed", color: "var(--my-orders-status-processing, #ea580c)" },
  pending: { labelKey: "statusPending", color: "var(--my-orders-status-processing, #d97706)" },
  cancelled: { labelKey: "statusCancelled", color: "#6b7280" },
};

function formatDate(dateStr, lang) {
  if (!dateStr) return "—";
  const locale = String(lang || "").startsWith("ar") ? "ar-EG" : "en-GB";
  const d = new Date(dateStr);
  return d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
}

function getItemCount(order) {
  if (!order?.items?.length) return 0;
  return order.items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
}

export default function MyOrders() {
  const { t, i18n } = useTranslation("global");
  const tr = React.useCallback(
    (key, defaultValue, opts = {}) =>
      t(`store.myOrders.${key}`, { defaultValue, ...opts }),
    [t]
  );
  const apiBaseUrl = axios.defaults.baseURL || "";
  const resolveImageUrl = (url) => resolveUrl(url, apiBaseUrl);
  const { isAuthenticated, loading: authLoading } = useStoreAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get("/orders/my")
      .then(({ data }) => {
        if (!cancelled) setOrders(data.orders || []);
      })
      .catch(() => {
        if (!cancelled) setOrders([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (authLoading || (isAuthenticated && loading)) {
    return (
      <div className="store-page store-my-orders-page">
        <div className="store-my-orders-loader">
          <BeatLoader color="#2a2520" size={12} />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <section className="store-page store-my-orders-page">
        <div className="store-my-orders-gate">
          <div className="store-my-orders-gate-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h2 className="store-my-orders-gate-title">{tr("signInTitle", "Sign in to see your orders")}</h2>
          <p className="store-my-orders-gate-text">{tr("signInText", "Use your account to view order history and track deliveries.")}</p>
          <div className="store-my-orders-gate-actions">
            <StoreSignIn />
            <Link to="/store" className="store-my-orders-btn store-my-orders-btn-secondary">
              {tr("browseProducts", "Browse products")}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (orders.length === 0) {
    return (
      <section className="store-page store-my-orders-page">
        <div className="store-my-orders-empty">
          <div className="store-my-orders-empty-illus" aria-hidden>
            <svg viewBox="0 0 120 120" fill="none" stroke="currentColor" strokeWidth="1.2">
              <path d="M40 45h40l-4 35H44L40 45z" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M50 45V35a10 10 0 0120 0v10" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="store-my-orders-empty-title">{tr("emptyTitle", "You haven't placed any orders yet.")}</h2>
          <p className="store-my-orders-empty-text">{tr("emptyText", "Start exploring our products and find something you'll love.")}</p>
          <div className="store-my-orders-empty-actions">
            <Link to="/store" className="store-my-orders-btn store-my-orders-btn-primary">
              {tr("browseProducts", "Browse products")}
            </Link>
            <Link to="/store/checkout" className="store-my-orders-btn store-my-orders-btn-secondary">
              {tr("viewCart", "View cart")}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="store-page store-my-orders-page">
      <div className="store-my-orders-inner container">
        <h1 className="store-my-orders-heading">{tr("title", "My Orders")}</h1>
        <ul className="store-my-orders-list">
          {orders.map((order) => {
            const raw = (order.status || "pending").toLowerCase();
            const statusKey = raw === "processing" ? "confirmed" : raw;
            const config = STATUS_CONFIG[statusKey] || STATUS_CONFIG.pending;
            const firstItem = order.items?.[0];
            const thumbUrl = firstItem?.imageUrl ? resolveImageUrl(firstItem.imageUrl) : null;
            const itemCount = getItemCount(order);
            return (
              <li key={order._id} className="store-my-orders-card">
                <div className="store-my-orders-card-top">
                  {thumbUrl ? (
                    <img src={thumbUrl} alt="" className="store-my-orders-card-thumb" />
                  ) : (
                    <div className="store-my-orders-card-thumb store-my-orders-card-thumb-placeholder" aria-hidden />
                  )}
                  <div className="store-my-orders-card-body">
                    <div className="store-my-orders-card-header">
                      <span className="store-my-orders-card-id" dir="ltr">
                        {tr("orderNumber", "Order #{{id}}", {
                          id: String(order._id).slice(-8).toUpperCase(),
                        })}
                      </span>
                      <span
                        className="store-my-orders-card-status"
                        style={{ backgroundColor: config.color, color: "#fff" }}
                      >
                        {tr(config.labelKey, statusKey)}
                      </span>
                    </div>
                    <div className="store-my-orders-card-meta">
                      <span dir="ltr">{formatDate(order.createdAt, i18n.language)}</span>
                      <span>{tr("itemCount", "{{count}} items", { count: itemCount })}</span>
                      <span>
                        <StoreMoneyDisplay
                          amount={order.totals?.total ?? 0}
                          currencyCode={order.totals?.currency || "SAR"}
                        />
                      </span>
                      <span>
                        {order.payment?.method === "cod"
                          ? tr("paymentCod", "Cash on delivery")
                          : tr("paymentOnline", "Online")}
                      </span>
                    </div>
                    <div className="store-my-orders-card-actions">
                      <Link to={`/store/my-orders/${order._id}`} className="store-my-orders-btn store-my-orders-btn-primary store-my-orders-btn-sm">
                        {tr("viewDetails", "View details")}
                      </Link>
                      <Link to={`/store/my-orders/${order._id}#track`} className="store-my-orders-btn store-my-orders-btn-outline store-my-orders-btn-sm">
                        {tr("trackOrder", "Track order")}
                      </Link>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
